import React from 'react';

interface Ad {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  link: string;
  tier: 'gold' | 'silver' | 'bronze';
  businessName: string;
  location: string;
}

interface SidebarAdsProps {
  className?: string;
  maxAds?: number;
}

const SidebarAds: React.FC<SidebarAdsProps> = ({ 
  className = '', 
  maxAds = 3 
}) => {
  // Mock ad data
  const mockAds: Ad[] = [
    {
      id: '1',
      title: 'Premium Beehive Kits',
      description: 'Complete starter kits for new beekeepers. Everything you need to get started!',
      imageUrl: '/api/placeholder/250/150',
      link: '#',
      tier: 'gold',
      businessName: 'Southern Bee Equipment Co.',
      location: 'Atlanta, GA'
    },
    {
      id: '2',
      title: 'Organic Wildflower Honey',
      description: 'Pure, unfiltered honey from Georgia wildflowers. Order online for fast delivery.',
      imageUrl: '/api/placeholder/250/150',
      link: '#',
      tier: 'silver',
      businessName: 'Georgia Pure Honey',
      location: 'Savannah, GA'
    },
    {
      id: '3',
      title: 'Queen Bee Pre-Orders',
      description: 'Italian & Carniolan queens available for spring delivery. Book now!',
      imageUrl: '/api/placeholder/250/150',
      link: '#',
      tier: 'bronze',
      businessName: 'Queen Bees of the South',
      location: 'Charlotte, NC'
    },
    {
      id: '4',
      title: 'Beekeeping Workshop',
      description: 'Learn from the experts! Monthly workshops for beginners and advanced beekeepers.',
      imageUrl: '/api/placeholder/250/150',
      link: '#',
      tier: 'silver',
      businessName: 'Bee Education Center',
      location: 'Nashville, TN'
    }
  ];

  const displayedAds = mockAds.slice(0, maxAds);

  const getTierStyling = (tier: string) => {
    switch (tier) {
      case 'gold':
        return {
          border: 'border-yellow-400',
          badge: 'bg-yellow-100 text-yellow-800',
          glow: 'shadow-yellow-100'
        };
      case 'silver':
        return {
          border: 'border-gray-400',
          badge: 'bg-gray-100 text-gray-800',
          glow: 'shadow-gray-100'
        };
      case 'bronze':
        return {
          border: 'border-orange-400',
          badge: 'bg-orange-100 text-orange-800',
          glow: 'shadow-orange-100'
        };
      default:
        return {
          border: 'border-gray-200',
          badge: 'bg-gray-100 text-gray-600',
          glow: 'shadow-gray-100'
        };
    }
  };

  const AdCard: React.FC<{ ad: Ad }> = ({ ad }) => {
    const styling = getTierStyling(ad.tier);
    
    return (
      <div className={`bg-white rounded-lg border-2 ${styling.border} ${styling.glow} shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer`}>
        <div className="relative">
          <img
            src={ad.imageUrl}
            alt={ad.title}
            className="w-full h-32 object-cover"
          />
          <div className="absolute top-2 right-2">
            <span className={`px-2 py-1 rounded-full text-xs font-bold ${styling.badge} uppercase`}>
              {ad.tier}
            </span>
          </div>
        </div>
        
        <div className="p-3">
          <h3 className="font-semibold text-gray-900 text-sm mb-1 line-clamp-1">
            {ad.title}
          </h3>
          
          <p className="text-xs text-gray-600 mb-2 line-clamp-2">
            {ad.description}
          </p>
          
          <div className="border-t border-gray-100 pt-2 mt-2">
            <p className="text-xs font-medium text-gray-800 line-clamp-1">
              {ad.businessName}
            </p>
            <p className="text-xs text-gray-500">
              {ad.location}
            </p>
          </div>
          
          <button className="w-full mt-3 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors duration-200">
            Learn More
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`sidebar-ads ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Featured Sponsors
        </h3>
        <p className="text-sm text-gray-600">
          Supporting our beekeeping community
        </p>
      </div>
      
      <div className="space-y-4">
        {displayedAds.map((ad) => (
          <AdCard key={ad.id} ad={ad} />
        ))}
      </div>
      
      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
        <h4 className="font-semibold text-gray-900 text-sm mb-2">
          Advertise with Us
        </h4>
        <p className="text-xs text-gray-600 mb-3">
          Reach thousands of beekeepers across the Southern US. Choose from our Gold, Silver, or Bronze advertising packages.
        </p>
        <button className="w-full px-3 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-xs font-medium rounded-md hover:from-blue-700 hover:to-purple-700 transition-all duration-200">
          Start Advertising
        </button>
      </div>
      
      <div className="mt-4 text-center">
        <p className="text-xs text-gray-500">
          Sponsored content helps support our platform
        </p>
      </div>
    </div>
  );
};

export default SidebarAds;
